import _ from 'lodash';
import { createSelector } from 'reselect';

import {
  AiChatActivityPhase,
  AiChatMessageRole,
  AiChatMessageStatus,
  AiChatPlanStatus,
  AiChatPlanStepStatus,
  AiPlanUserOutcome,
  PENDING_TOOL_CONFIRMATION_STATUSES,
  PENDING_USER_QUESTION_STATUSES,
  ToolActivityStatus,
} from 'constants/aiChat';

const selectAiChat = state => state.aiChat;

export const selectAiChatSession = createSelector(selectAiChat, ({ session }) => session);

export const selectAiChatSessionId = createSelector(
  selectAiChat,
  ({ sessionId, session }) => sessionId ?? session?.sessionId ?? session?.id ?? null,
);

// DEV-4392: the loan the active chat session belongs to. Lets the reconnect
// handler re-fetch chat messages without threading loanId through the SignalR
// connector. Sourced from the route-supplied loanId persisted on getMessages
// (always present — it is the same value the panel loads history with) and only
// falls back to the session DTO, whose `loanId` serialization is not relied on.
export const selectAiChatLoanId = createSelector(
  selectAiChat,
  ({ loanId, session }) => loanId ?? session?.loanId ?? null,
);

export const selectAiChatConfiguration = createSelector(
  selectAiChat,
  ({ configuration }) => configuration,
);

const selectAiChatToolSlaGroups = createSelector(
  selectAiChatConfiguration,
  configuration => configuration?.toolSlaGroups ?? null,
);

// DEV-3192: resolves the per-tool SLA-group setting ({ mode, slaGroupId? }) for
// a pending tool confirmation from the active chat configuration. Tool names are
// matched case-insensitively to mirror the backend's keyed lookup. Returns null
// for tools without an SLA setting (i.e. non-condition tools).
export const selectAiChatToolSlaSetting = (state, toolName) => {
  const toolSlaGroups = selectAiChatToolSlaGroups(state);

  if (!toolSlaGroups || !toolName) {
    return null;
  }

  const matchKey = Object.keys(toolSlaGroups).find(
    key => key.toLowerCase() === toolName.toLowerCase(),
  );

  return matchKey ? toolSlaGroups[matchKey] : null;
};

export const selectAiChatLastMessageDate = createSelector(
  selectAiChat,
  ({ lastMessageDate }) => lastMessageDate,
);

export const selectAiChatMessages = createSelector(selectAiChat, ({ messages }) => messages);

export const selectAiChatIsLoading = createSelector(selectAiChat, ({ isLoading }) => isLoading);

export const selectAiChatIsSending = createSelector(selectAiChat, ({ isSending }) => isSending);

export const selectAiChatError = createSelector(selectAiChat, ({ error }) => error);

export const selectAiChatIsPanelOpen = createSelector(
  selectAiChat,
  ({ isPanelOpen }) => isPanelOpen,
);

export const selectIsDrawerOpen = selectAiChatIsPanelOpen;

export const selectAiChatUnreadCount = createSelector(
  selectAiChat,
  ({ unreadCount }) => unreadCount ?? 0,
);

export const selectUnreadCount = selectAiChatUnreadCount;

export const selectEligibleConfigurations = createSelector(
  selectAiChat,
  ({ eligibleConfigurations }) => eligibleConfigurations ?? [],
);

export const selectActiveConfiguration = selectAiChatConfiguration;

export const selectIsLoadingEligibleConfigurations = createSelector(
  selectAiChat,
  ({ isLoadingEligibleConfigurations }) => !!isLoadingEligibleConfigurations,
);

export const selectIsSwitchingConfiguration = createSelector(
  selectAiChat,
  ({ isSwitchingConfiguration }) => !!isSwitchingConfiguration,
);

export const selectAiChatHasMessages = createSelector(
  selectAiChatMessages,
  messages => !!messages.length,
);

export const selectAiChatLastMessage = createSelector(selectAiChatMessages, messages =>
  messages.length ? messages[messages.length - 1] : null,
);

export const selectAiChatIsAssistantTyping = createSelector(
  selectAiChatLastMessage,
  lastMessage =>
    lastMessage?.role === AiChatMessageRole.ASSISTANT &&
    lastMessage?.status === AiChatMessageStatus.STREAMING,
);

export const selectAiChatIsAwaitingAssistantResponse = createSelector(
  selectAiChat,
  ({ isAwaitingAssistantResponse }) => !!isAwaitingAssistantResponse,
);

export const selectAiChatAwaitingAssistantStartedAt = createSelector(
  selectAiChat,
  ({ awaitingAssistantStartedAt }) => awaitingAssistantStartedAt ?? null,
);

export const selectAiChatAwaitingAssistantMessageId = createSelector(
  selectAiChat,
  ({ awaitingAssistantMessageId }) => awaitingAssistantMessageId ?? null,
);

export const selectAiChatAwaitingAssistantLastActivityAt = createSelector(
  selectAiChat,
  ({ awaitingAssistantLastActivityAt }) => awaitingAssistantLastActivityAt ?? null,
);

export const selectAiChatIsAssistantSlow = createSelector(
  selectAiChat,
  ({ isAssistantSlow }) => !!isAssistantSlow,
);

export const selectAiChatShouldShowThinkingIndicator = createSelector(
  [selectAiChatIsAssistantTyping, selectAiChatIsAwaitingAssistantResponse],
  (isTyping, isAwaitingResponse) => isTyping || isAwaitingResponse,
);

export const selectAiChatFailedMessages = createSelector(selectAiChatMessages, messages =>
  messages.filter(msg => msg.status === AiChatMessageStatus.FAILED),
);

export const selectAiChatHasFailedMessages = createSelector(
  selectAiChatFailedMessages,
  failedMessages => !!failedMessages.length,
);

export const selectAiChatExecutionPlan = createSelector(
  selectAiChat,
  ({ executionPlan }) => executionPlan,
);

export const selectAiChatHasExecutionPlan = createSelector(
  selectAiChatExecutionPlan,
  executionPlan => !!executionPlan,
);

export const selectAiChatExecutionPlans = createSelector(
  selectAiChat,
  ({ executionPlans }) => executionPlans ?? {},
);

export const selectAiChatPlanByMessageId = (state, messageId) => {
  if (_.isNil(messageId)) {
    return null;
  }

  const executionPlans = selectAiChatExecutionPlans(state);

  return executionPlans[messageId] ?? null;
};

export const selectExecutionPlanByExecutionId = (state, executionId) => {
  if (_.isNil(executionId)) {
    return null;
  }

  const executionPlans = selectAiChatExecutionPlans(state);

  return executionPlans[executionId] ?? null;
};

export const selectActionPlanExpansionsByExecutionId = (state, executionId) => {
  if (_.isNil(executionId)) {
    return null;
  }

  return state.aiChat?.actionPlanExpansions?.[executionId] ?? null;
};

const EMPTY_CITATIONS = [];

export const selectAiChatCitations = createSelector(
  selectAiChat,
  ({ citations }) => citations ?? {},
);

export const selectAiChatCitationsBlobLoaded = createSelector(
  selectAiChat,
  ({ citationsBlobLoaded }) => citationsBlobLoaded ?? {},
);

export const selectAiChatCitationsByMessageId = (state, messageId) => {
  if (_.isNil(messageId)) {
    return EMPTY_CITATIONS;
  }

  const citations = selectAiChatCitations(state);

  return citations[messageId] ?? EMPTY_CITATIONS;
};

export const selectAiChatGuidelineFullView = createSelector(
  selectAiChat,
  ({ guidelineFullView }) => guidelineFullView,
);

export const selectAiChatGuidelineCache = createSelector(
  selectAiChat,
  ({ guidelineCache }) => guidelineCache,
);

export const selectAiChatIsLoadingGuideline = createSelector(
  selectAiChat,
  ({ isLoadingGuideline }) => isLoadingGuideline,
);

export const selectAiChatGuidelineContent = createSelector(
  [selectAiChatGuidelineFullView, selectAiChatGuidelineCache],
  (guidelineFullView, guidelineCache) =>
    guidelineFullView ? guidelineCache[guidelineFullView.id] : null,
);

export const selectAiChatPendingToolExecution = createSelector(
  selectAiChat,
  ({ pendingToolExecution }) => pendingToolExecution,
);

export const selectAiChatHasPendingToolExecution = createSelector(
  selectAiChatPendingToolExecution,
  pendingToolExecution => !!pendingToolExecution,
);

export const selectAiChatToolExecutionHistory = createSelector(
  selectAiChat,
  ({ toolExecutionHistory }) => toolExecutionHistory,
);

export const selectAiChatIsConnected = createSelector(
  selectAiChat,
  ({ isConnected }) => isConnected,
);

const EMPTY_MESSAGE_CONTENT_BLOCKS = [];

export const selectAiChatMessageContentBlocks = createSelector(
  selectAiChat,
  ({ messageContentBlocks }) => messageContentBlocks ?? {},
);

export const selectAiChatMessageContentBlocksByMessageId = (state, messageId) => {
  if (_.isNil(messageId)) {
    return EMPTY_MESSAGE_CONTENT_BLOCKS;
  }

  const map = selectAiChatMessageContentBlocks(state);

  return map[messageId] ?? EMPTY_MESSAGE_CONTENT_BLOCKS;
};

const EMPTY_PENDING_TOOL_CONFIRMATIONS = [];

export const selectAiChatPendingToolConfirmations = createSelector(
  selectAiChat,
  ({ pendingToolConfirmations }) => pendingToolConfirmations ?? {},
);

export const selectAiChatPendingToolConfirmationsByMessageId = (state, messageId) => {
  if (_.isNil(messageId)) {
    return EMPTY_PENDING_TOOL_CONFIRMATIONS;
  }

  const map = selectAiChatPendingToolConfirmations(state);

  return map[messageId] ?? EMPTY_PENDING_TOOL_CONFIRMATIONS;
};

const EMPTY_PENDING_USER_QUESTIONS = [];

export const selectAiChatPendingUserQuestions = createSelector(
  selectAiChat,
  ({ pendingUserQuestions }) => pendingUserQuestions ?? {},
);

export const selectAiChatPendingUserQuestionsByMessageId = (state, messageId) => {
  if (_.isNil(messageId)) {
    return EMPTY_PENDING_USER_QUESTIONS;
  }

  const map = selectAiChatPendingUserQuestions(state);

  return map[messageId] ?? EMPTY_PENDING_USER_QUESTIONS;
};

export const selectAiChatActivePendingUserQuestionByMessageId = (state, messageId) => {
  const list = selectAiChatPendingUserQuestionsByMessageId(state, messageId);

  return list.find(question => PENDING_USER_QUESTION_STATUSES.has(question.status)) ?? null;
};

export const selectAiChatMessageOutcomeByMessageId = (state, messageId) => {
  if (_.isNil(messageId)) {
    return AiPlanUserOutcome.NO_FEEDBACK;
  }

  const messages = selectAiChatMessages(state);
  const message = messages.find(({ id }) => id === messageId);

  return message?.userOutcome ?? AiPlanUserOutcome.NO_FEEDBACK;
};

export const selectAiChatRunningToolMessage = createSelector(
  selectAiChatMessages,
  messages =>
    _.findLast(
      messages,
      message =>
        message.role === AiChatMessageRole.TOOL && message.status === ToolActivityStatus.RUNNING,
    ) ?? null,
);

const findRunningPlanStep = plan =>
  (plan?.steps ?? []).find(
    step => step.status === AiChatPlanStepStatus.IN_PROGRESS && !_.isNil(step.toolName),
  ) ?? null;

export const selectAiChatRunningPlanStep = createSelector(selectAiChatExecutionPlans, plans =>
  _.reduce(plans, (found, plan) => found ?? findRunningPlanStep(plan), null),
);

export const selectAiChatHasRunningTool = createSelector(
  [selectAiChatRunningToolMessage, selectAiChatRunningPlanStep],
  (toolMessage, planStep) => !!toolMessage || !!planStep,
);

export const selectAiChatRunningToolFriendlyName = createSelector(
  [selectAiChatRunningToolMessage, selectAiChatRunningPlanStep],
  (toolMessage, planStep) =>
    toolMessage?.friendlyName ??
    toolMessage?.toolName ??
    planStep?.label ??
    planStep?.description ??
    planStep?.toolName ??
    null,
);

export const selectAiChatHasActiveExecutionPlan = createSelector(
  selectAiChatExecutionPlans,
  plans => Object.values(plans).some(plan => plan?.status === AiChatPlanStatus.IN_PROGRESS),
);

export const selectAiChatHasPendingToolConfirmation = createSelector(
  selectAiChatPendingToolConfirmations,
  pendingMap =>
    Object.values(pendingMap).some(list =>
      list.some(confirmation => PENDING_TOOL_CONFIRMATION_STATUSES.has(confirmation.status)),
    ),
);

export const selectAiChatHasPendingUserQuestion = createSelector(
  selectAiChatPendingUserQuestions,
  pendingMap =>
    Object.values(pendingMap).some(list =>
      list.some(question => PENDING_USER_QUESTION_STATUSES.has(question.status)),
    ),
);

export const selectAiChatHasPendingApproval = createSelector(
  [
    selectAiChatHasPendingToolExecution,
    selectAiChatHasPendingToolConfirmation,
    selectAiChatHasPendingUserQuestion,
  ],
  (hasFrontendPending, hasBackendPending, hasPendingQuestion) =>
    hasFrontendPending || hasBackendPending || hasPendingQuestion,
);

export const selectAiChatPlanForAwaitingMessage = createSelector(
  [selectAiChatExecutionPlans, selectAiChatAwaitingAssistantMessageId],
  (plans, messageId) => (_.isNil(messageId) ? null : (plans[messageId] ?? null)),
);

const selectAiChatAwaitingPhase = createSelector(
  [
    selectAiChatPlanForAwaitingMessage,
    selectAiChatHasActiveExecutionPlan,
    selectAiChatIsAwaitingAssistantResponse,
    selectAiChatIsAssistantSlow,
  ],
  (awaitingPlan, hasActivePlan, isAwaiting, isSlow) => {
    if (!isAwaiting) {
      return AiChatActivityPhase.IDLE;
    }

    if (awaitingPlan || hasActivePlan) {
      return AiChatActivityPhase.PLANNING;
    }

    if (isSlow) {
      return AiChatActivityPhase.STILL_THINKING;
    }

    return AiChatActivityPhase.THINKING;
  },
);

export const selectAiChatActivityPhase = createSelector(
  [
    selectAiChatHasPendingApproval,
    selectAiChatHasRunningTool,
    selectAiChatIsAssistantTyping,
    selectAiChatAwaitingPhase,
  ],
  (hasApproval, hasRunningTool, isTyping, awaitingPhase) => {
    if (hasApproval) {
      return AiChatActivityPhase.AWAITING_APPROVAL;
    }

    if (awaitingPhase === AiChatActivityPhase.IDLE) {
      return AiChatActivityPhase.IDLE;
    }

    if (hasRunningTool) {
      return AiChatActivityPhase.RUNNING_TOOL;
    }

    if (isTyping) {
      return AiChatActivityPhase.WRITING;
    }

    return awaitingPhase;
  },
);

export const selectAiChatIsAnyMessageInFlight = createSelector(
  selectAiChatActivityPhase,
  phase => phase !== AiChatActivityPhase.IDLE,
);
